import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, History, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { formatDateTime } from '../lib/dateUtils';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HistoryDialog = ({ vehicle, token, onClose }) => {
  const [logs, setLogs] = useState([]);
  useEffect(() => {
    if (!vehicle) return;
    axios.get(`${API_URL}/admin/subscriptions/history`, { params: { vehicle_id: vehicle.vehicle_id }, headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setLogs(res.data))
      .catch(() => toast.error('Failed to load history'));
  }, [vehicle, token]);

  return (
    <Dialog open={!!vehicle} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="subscription-history-dialog">
        <DialogHeader>
          <DialogTitle>History · {vehicle?.registration_number}</DialogTitle>
          <DialogDescription>{vehicle?.operator_name} — every tick and untick, newest first.</DialogDescription>
        </DialogHeader>
        {logs.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="subscription-history-empty">No ticks recorded yet.</p>
        ) : (
          <ul className="space-y-2">
            {logs.map((l) => (
              <li key={l.log_id} className="flex items-center justify-between gap-2 p-3 border rounded-lg text-sm" data-testid={`history-row-${l.log_id}`}>
                <div>
                  <p className="font-medium">{l.month}</p>
                  <p className="text-xs text-muted-foreground">{formatDateTime(l.ticked_at)} · {l.ticked_by_name} ({l.ticked_by_role?.replace('_', ' ')})</p>
                </div>
                <Badge className={l.paid ? 'bg-emerald-600' : 'bg-red-600'}>{l.paid ? 'Paid' : 'Not paid'}</Badge>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function Subscriptions() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [month, setMonth] = useState('');
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [confirmFor, setConfirmFor] = useState(null);

  const load = useCallback(async (m) => {
    try {
      const res = await axios.get(`${API_URL}/admin/subscriptions`, { params: m ? { month: m } : {}, headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
      setMonth(res.data.month);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load subscriptions');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const requestToggle = (v) => {
    if (month < data.current_month) setConfirmFor(v);
    else toggle(v);
  };

  const toggle = async (v) => {
    setConfirmFor(null);
    setBusyId(v.vehicle_id);
    try {
      const res = await axios.put(`${API_URL}/admin/subscriptions/${v.vehicle_id}`, { month, paid: !v.paid }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      load(month);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setBusyId(null);
    }
  };

  const rows = (data?.vehicles || []).filter((v) => {
    const f = filter.trim().toLowerCase().replace(/\s/g, '');
    return !f || `${v.operator_name}${v.registration_number}`.toLowerCase().replace(/\s/g, '').includes(f);
  });
  const paidCount = (data?.vehicles || []).filter((v) => v.paid).length;
  const isFuture = !!data && month > data.current_month;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white" data-testid="subscriptions-page">
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="subscriptions-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Monthly subscriptions</h1>
            <p className="text-sm text-muted-foreground">Tick “Paid” for each vehicle, one month at a time.</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-4">
        <div className="bg-white border rounded-2xl p-4 flex flex-col sm:flex-row gap-3 sm:items-end">
          <div>
            <Label>Month</Label>
            <Input type="month" value={month} max={data?.current_month} onChange={(e) => e.target.value && load(e.target.value)} className="w-full sm:w-48" data-testid="subscription-month-input" />
          </div>
          <div className="flex-1">
            <Label>Search</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" placeholder="Operator or number plate" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="subscription-filter-input" />
            </div>
          </div>
          <div className="text-sm font-medium" data-testid="subscription-summary">
            {isFuture ? <span className="text-amber-700" data-testid="subscription-future-note">Future month — can't be ticked yet</span> : `${paidCount} of ${data?.vehicles?.length || 0} vehicles paid`}
          </div>
        </div>

        <div className="space-y-2" data-testid="subscription-list">
          {data && rows.length === 0 && <p className="text-sm text-muted-foreground p-4" data-testid="subscription-empty">No vehicles found.</p>}
          {rows.map((v) => (
            <div key={v.vehicle_id} className={`bg-white border rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-3 justify-between ${v.paid ? 'border-emerald-200' : ''}`} data-testid={`subscription-row-${v.registration_number}`}>
              <label className="flex items-center gap-3 cursor-pointer min-w-0">
                <Checkbox checked={v.paid} disabled={busyId === v.vehicle_id || isFuture} onCheckedChange={() => requestToggle(v)} data-testid={`subscription-paid-${v.registration_number}`} />
                <div className="min-w-0">
                  <p className="font-semibold">{v.registration_number} <span className="text-sm font-normal text-muted-foreground">{v.make} {v.model}</span></p>
                  <p className="text-sm text-muted-foreground truncate">{v.operator_name}{v.operator_email ? ` · ${v.operator_email}` : ''}</p>
                </div>
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={v.paid ? 'bg-emerald-600' : 'bg-red-600'} data-testid={`subscription-status-${v.registration_number}`}>{v.paid ? 'Paid' : 'Not paid'}</Badge>
                {v.ticked_at && <span className="text-xs text-muted-foreground">{v.paid ? 'Ticked' : 'Unticked'} {formatDateTime(v.ticked_at)} · {v.ticked_by_name}</span>}
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setHistoryFor(v)} data-testid={`subscription-history-${v.registration_number}`}>
                  <History className="w-4 h-4 mr-1" /> History
                </Button>
              </div>
            </div>
          ))}
        </div>
      </main>
      <HistoryDialog vehicle={historyFor} token={token} onClose={() => setHistoryFor(null)} />
      <Dialog open={!!confirmFor} onOpenChange={(o) => { if (!o) setConfirmFor(null); }}>
        <DialogContent data-testid="subscription-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Change a past month?</DialogTitle>
            <DialogDescription>
              Are you sure you want to mark <strong>{confirmFor?.registration_number}</strong> as <strong>{confirmFor?.paid ? 'Not paid' : 'Paid'}</strong> for <strong>{month}</strong>? Your name and the time will be saved in the history.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmFor(null)} data-testid="subscription-confirm-cancel">Cancel</Button>
            <Button onClick={() => toggle(confirmFor)} data-testid="subscription-confirm-yes">Yes, change it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
